import type { Branding } from '@seek/libs-shared';
import type { MonthPicker } from 'braid-design-system';

import type { ReviewFormContext } from '../shared/tracking/writeAReview/trackingEvents';

import type { Rating } from './components/StarSelector/StarSelector';
import type { SupportedCurrency } from './questions/GroupedQuestions/SalaryQuestions/SalaryRanges';

export const Progress = {
  OverallRating: 1,
  YourRole: 2,
  YourExperience: 3,
  YourSalary: 4,
  YourFeedback: 5,
  SubmitSuccess: 6,
  SimplifiedPage: 7,
} as const satisfies Record<string, number>;
export type Progress = (typeof Progress)[keyof typeof Progress];

export const ExperimentTwoProgress = {
  OverallRating: 1,
  WorkLifeBalance: 2,
  PerksAndBenefits: 3,
  Salary: 4,
  CareerDevelopment: 5,
  Management: 6,
  DiversityAndEqualOpportunity: 7,
  SubmitSuccess: 8,
} as const satisfies Record<string, number>;
export type ExperimentTwoProgress =
  (typeof ExperimentTwoProgress)[keyof typeof ExperimentTwoProgress];

export type { ReviewFormContext };
export type WorkType = 'Full Time' | 'Part Time' | 'Contract' | 'Casual';

export type MonthPickerValue = Parameters<
  Parameters<typeof MonthPicker>[0]['onChange']
>[0];

export const salaryTypeValues = [
  'Monthly',
  'AnnualPackage',
  'HourlyRate',
] as const;

export type SalaryType = (typeof salaryTypeValues)[number];

export type SalaryRating = 'Below Par' | 'Fair' | 'Generous';

export interface Salary {
  currency?: SupportedCurrency;
  includeBonus?: boolean;
  maximum?: number;
  minimum?: number;
}

export interface ReviewPayload {
  branding?: Branding;
  careerOpportunityRating?: Rating;
  companyId?: string;
  companyName?: string;
  companyProfilePublished?: boolean;
  diversityRating?: Rating;
  executiveManagementRating?: Rating;
  fullLocation?: string;
  isRecommended?: boolean;
  isStillWorkHere?: boolean;
  isWorkHistoryPrefilled?: boolean;
  jobTitleId?: string;
  jobTitleText?: string;
  locationId?: number;
  organisationId?: string;
  overallExperienceDetails?: string;
  overallRating?: Rating;
  perksAndBenefitsRating?: Rating;
  roleId?: string;
  salary?: Salary;
  salaryRating?: SalaryRating;
  salaryType?: SalaryType;
  theChallenges?: string;
  theGoodThings?: string;
  title?: string;
  workEnvironmentRating?: Rating;
  workHistory?: {
    from?: MonthPickerValue;
    to?: MonthPickerValue;
  };
  workHistoryFrom?: MonthPickerValue;
  workHistoryTo?: MonthPickerValue;
  workLifeBalanceRating?: Rating;
  workType?: WorkType;
}

export interface PageProps {
  pageNumber: Progress;
  review: ReviewPayload;
  reviewFormContext: ReviewFormContext;
  setPage: (page: Progress) => void;
  setter: React.Dispatch<React.SetStateAction<ReviewPayload>>;
  experimentNumber?: number;
  onAlertClosed?: () => void;
  onSubmit?: () => void;
}

export interface QuestionProps<T = string> {
  onChange: (value: T) => void;
  message?: string;
  tone?: 'critical' | 'neutral';
  value?: T;
}
